import React from "react";
import { Link } from "react-router-dom";
import { BOOKING_URL } from "@/lib/site";
import { BookLink } from "@/components/site/BookLink";
import { PageHero, Eyebrow } from "@/components/site/Section";

const values = [
  { title: "Expert Guidance", desc: "Clear recommendations based on your needs, comfort, and desired outcome." },
  { title: "Safety First", desc: "Professional protocols, trusted products, and careful treatment planning." },
  { title: "Natural Results", desc: "Balanced enhancements that complement your individual features." },
];

const approach = [
  { title: "Evidence-Based Care", desc: "Medical guidance backed by clinical judgment and safety-first planning." },
  { title: "Facial Harmony", desc: "Treatments are considered as part of the whole face, not isolated features." },
  { title: "Private Experience", desc: "One-on-one attention in a calm, patient-centered setting." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Seraphina"
        title="Personalized Care. Exceptional Results."
        subtitle="A welcoming, patient-centered approach to aesthetics and wellness."
      />

      <section className="py-20 bg-[var(--cream)]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div className="rounded-2xl overflow-hidden soft-card">
            <img src="/assets/reception.jpg" alt="Seraphina Medspa reception" className="w-full h-full object-cover" />
          </div>
          <div>
            <h2 className="font-display text-4xl text-[var(--charcoal)] leading-tight">Care That Begins With Listening</h2>
            <p className="text-[var(--charcoal-soft)] leading-relaxed mt-5">
              At Seraphina Medspa, every treatment begins with your goals. We combine modern technology,
              thoughtful consultation, and a conservative approach to create results that feel natural and personal.
            </p>
            <p className="text-[var(--charcoal-soft)] leading-relaxed mt-4">
              Our focus is not changing who you are. It is helping you feel like the most confident version of yourself.
            </p>
            <div className="grid sm:grid-cols-3 gap-5 mt-8">
              {values.map((v) => (
                <div key={v.title}>
                  <h4 className="font-display text-xl text-[var(--charcoal)]">{v.title}</h4>
                  <p className="text-sm text-[var(--charcoal-soft)] mt-1">{v.desc}</p>
                </div>
              ))}
            </div>
            <Link to="/contact" className="btn-outline-gold rounded-full px-7 py-3.5 inline-block mt-8">Contact Us</Link>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-20 bg-[var(--cream-2)] border-y border-[var(--line)]">
        <div className="max-w-[900px] mx-auto px-5 lg:px-10 text-center">
          <Eyebrow className="mb-3">Meet the Founder</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--charcoal)]">Meet Elizabeth, APRN, AGACNP-BC</h2>
          <p className="text-[var(--gold)] italic font-display text-lg mt-2">Founder &amp; Clinical Director, Seraphina Collective</p>
          <div className="text-[var(--charcoal-soft)] leading-relaxed mt-6 space-y-4 text-left">
            <p>Welcome to Seraphina Collective! I am Elizabeth, a Board-Certified Nurse Practitioner specializing in Adult-Gerontology Acute Care.</p>
            <p>My medical career was built in high-stakes clinical settings, managing complex patient health with absolute precision and safety. My passion for anatomical detail led me to pursue advanced, elite training in medical aesthetics, earning additional board credentials through the prestigious American Academy of Facial Esthetics (AAFE).</p>
            <p>As an APRN, my approach to aesthetic rejuvenation goes far beyond the surface. I view this medicine through a deep structural lens—combining a master-level understanding of underlying facial anatomy with state-of-the-art technologies like our FrioLift Thermal Shock system and medical weight loss programs.</p>
            <p>I don't just offer these cutting-edge wellness and anti-aging treatments; I am a dedicated fan and user of them myself. I strictly curate our service menu to include only the highest-quality, non-surgical modalities that deliver visible, safe, and long-lasting results. When I'm not in our private, one-on-one clinical suite tailoring bespoke treatment plans for my local patients, you can find me enjoying life as a wife, proud mom to a wonderful grown young man, and an absolute doting parent to my little Maltese dog.</p>
            <p>I founded Seraphina Collective to escape the rushed, crowded environment of traditional commercial clinics. Here, you get my undivided attention, rigorous medical oversight, and an ultra-private, luxury experience designed to make you feel completely confident in your skin. I cannot wait to partner with you on your aesthetic and wellness journey!</p>
          </div>
          <BookLink label="About Founder" href={BOOKING_URL} className="btn-gold rounded-full px-8 py-3.5 inline-block mt-8">
            Book a Consultation
          </BookLink>
        </div>
      </section>

      {/* Approach */}
      <section className="py-20 bg-[var(--cream)]">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10 text-center">
          <Eyebrow className="mb-3">The Seraphina Approach</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--charcoal)]">Natural Results With a Whole-Face Plan</h2>
          <p className="text-[var(--charcoal-soft)] leading-relaxed mt-5 max-w-2xl mx-auto">
            Seraphina Collective was created for patients who want a more private, thoughtful experience than a
            rushed clinic visit. Every recommendation is made with balance, safety, and long-term maintenance in mind.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 mt-12 text-left">
            {approach.map((a) => (
              <div key={a.title} className="soft-card rounded-2xl p-6">
                <h4 className="font-display text-xl text-[var(--charcoal)]">{a.title}</h4>
                <p className="text-sm text-[var(--charcoal-soft)] mt-2">{a.desc}</p>
              </div>
            ))}
          </div>
          <Link to="/services" className="btn-outline-gold rounded-full px-7 py-3.5 inline-block mt-10">Explore Services</Link>
        </div>
      </section>
    </div>
  );
}
